import { Table } from 'antd';
import { constants } from 'config/constants';
import _, { uniqBy } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { inventoryActions } from 'reduxStore/inventory/slice';
import { InventoryTTLockActions } from 'zComponents/atoms';
import { AssignInventoryAssets, EditDeviceLocationModal, UnassignInventoryAssets } from 'zComponents/molecules';
import { Button, Col, Input, Row } from 'zui/Antd';

const InventoryLocksTable = ({ workspaces }) => {
	//Hooks
	const dispatch = useDispatch();

	//Local state
	const [inputValue, setInputValue] = useState('');
	const [searchText, setSearchText] = useState('');
	const [filteredInfo, setFilteredInfo] = useState({});
	const [selectedInventoryUIDs, setSelectedInventoryUIDs] = useState([]);
	const [selectedDevice, setSelectedDevice] = useState({});
	const [openAssignModal, setOpenAssignModal] = useState(false);
	const [visibleEditLocation, setVisibleEditLocation] = useState(false);
	const [openUnassignModal, setOpenUnassignModal] = useState(false);

	const { locks: inventoryLocks } = useSelector((state) => state.Inventory);

	// Debounced search handler
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedSearch = useCallback(
		_.debounce((searchString) => {
			setSearchText(searchString);
		}, 300),
		[]
	);

	const handleChangeInput = (e) => {
		const value = e.target.value;
		setInputValue(value);
		debouncedSearch(value);
	};

	const filteredData = useMemo(() => {
		const query = searchText.toLowerCase();
		return inventoryLocks.filter(
			(object) =>
				object['inventoryName']?.toLowerCase().includes(query) ||
				object['lockID']?.toString().toLowerCase().includes(query) ||
				object['assetID']?.toString().toLowerCase().includes(query)
		);
	}, [inventoryLocks, searchText]);

	const columns = [
		{
			title: 'ID',
			dataIndex: 'lockID',
			key: 'lockID',
			width: 120,

			filters: uniqBy(
				inventoryLocks.map((lock) => {
					return {
						text: lock['lockID'],
						value: lock['lockID']
					};
				}),
				'text'
			),
			filterMode: 'menu',
			onFilter: (value, record) => record['lockID'] === value,
			filterSearch: true,
			filteredValue: filteredInfo['lockID'] || null
		},
		{
			title: 'Asset ID',
			dataIndex: 'assetID',
			key: 'assetID',
			width: 150,
			ellipsis: true,
			filters: uniqBy(
				inventoryLocks.map((lock) => {
					return {
						text: lock['assetID'],
						value: lock['assetID']
					};
				}),
				'text'
			),
			filterMode: 'menu',
			onFilter: (value, record) => record['assetID'] === value,
			filterSearch: true,
			filteredValue: filteredInfo['assetID'] || null
		},
		{
			title: 'Inventory ID',
			dataIndex: 'inventoryUID',
			key: 'inventoryUID',
			ellipsis: true,
			width: 150,
			filters: uniqBy(
				inventoryLocks.map((lock) => {
					return {
						text: lock['inventoryUID'],
						value: lock['inventoryUID']
					};
				}),
				'text'
			),
			filterMode: 'menu',
			onFilter: (value, record) => record['inventoryUID'] === value,
			filterSearch: true,
			filteredValue: filteredInfo['inventoryUID'] || null
		},

		{
			title: 'Electric Charge',
			dataIndex: 'electric Quantity',
			key: 'Electric Charge',
			ellipsis: true,
			width: 150
		},
		{
			title: 'Lock Name',
			dataIndex: 'inventoryName',
			key: 'inventoryName',
			ellipsis: true,
			width: 150,
			filters: uniqBy(
				inventoryLocks.map((lock) => {
					return {
						text: lock['inventoryName'],
						value: lock['inventoryName']
					};
				}),
				'text'
			),
			filterMode: 'menu',
			onFilter: (value, record) => record['inventoryName'] === value,
			filterSearch: true,
			filteredValue: filteredInfo['inventoryName'] || null
		},
		{
			title: 'Lock Group',
			dataIndex: 'lock Group Name',
			key: 'lock Group Name',
			ellipsis: true,
			width: 150,
			filters: uniqBy(
				inventoryLocks.map((lock) => {
					return {
						text: lock['lock Group Name'],
						value: lock['lock Group Name']
					};
				}),
				'text'
			),
			filterMode: 'menu',
			onFilter: (value, record) => record['lock Group Name'] === value,
			filterSearch: true,
			filteredValue: filteredInfo['lock Group Name'] || null
		},

		{
			title: 'Workspace',
			dataIndex: 'orgName',
			key: 'orgName',
			ellipsis: true,
			fixed: 'right',
			width: 150,
			filters: uniqBy(
				inventoryLocks.map((gateway) => {
					return {
						text: gateway['orgName'],
						value: gateway['orgName']
					};
				}),
				'text'
			),
			filterMode: 'menu',
			onFilter: (value, record) => record['orgName'] === value,
			filterSearch: true,
			filteredValue: filteredInfo['orgName'] || null
		},
		{
			title: 'Actions',
			dataIndex: '',
			key: 'actions',
			fixed: 'right',
			width: 250,
			render: (text, record) => (
				<InventoryTTLockActions
					record={record}
					selectedInventoryUIDs={selectedInventoryUIDs}
					setSelectedInventoryUIDs={setSelectedInventoryUIDs}
					setSelectedDevice={setSelectedDevice}
					setVisibleEditLocation={setVisibleEditLocation}
					setOpenAssignModal={setOpenAssignModal}
					setOpenUnassignModal={setOpenUnassignModal}
				/>
			)
		}
	];

	const clearFilters = () => {
		setFilteredInfo({});
		setInputValue('');
		setSearchText('');
	};

	const handleChange = (_pagination, filters) => {
		setFilteredInfo(filters);
	};

	return (
		<div style={{ overflow: 'hidden', padding: '0px 10px 10px 10px' }}>
			<Row justify='start'>
				<Col lg={24} md={24} sm={24} xs={24}>
					<div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
						<Button size={'small'} onClick={clearFilters}>
							Clear Filter(s)
						</Button>
					</div>
					<Input
						style={{ marginBottom: '10px' }}
						placeholder='Search Lock Name, ID Or Mac Address'
						allowClear
						value={inputValue}
						onChange={handleChangeInput}
					/>
				</Col>
			</Row>
			<Row gutter={24} justify='start'>
				<Col lg={24} md={24} sm={24} xs={24}>
					<Table
						columns={columns}
						pageSize={500}
						dataSource={filteredData}
						onChange={handleChange}
						rowSelection={{
							selectedRowKeys: selectedInventoryUIDs.map((d) => d.inventoryUID),
							onChange: (selectedRowKeys, selectedRows) => {
								const firstSelected = selectedRows[0];
								if (!firstSelected) return;

								const firstIsAssigned = !!firstSelected.orgOrgID;

								const filteredRows = selectedRows.filter((row) => !!row.orgOrgID === firstIsAssigned);

								const inventoryDevices = filteredRows.map((row) => ({
									inventoryUID: row.inventoryUID,
									deviceID: row.assetID || null,
									orgID: row.orgOrgID || null,
									name: row.inventoryName
								}));

								setSelectedInventoryUIDs(inventoryDevices);
							},
							getCheckboxProps: (record) => {
								const isAssigned = !!record.orgOrgID;
								const isLocked =
									selectedInventoryUIDs.length > 0 && !!selectedInventoryUIDs[0].orgID !== isAssigned;

								return {
									disabled: !record.inventoryUID || isLocked
								};
							}
						}}
						onRow={(record) => ({
							onClick: () => {
								setSelectedInventoryUIDs((prev) => {
									const exists = prev.some((item) => item.inventoryUID === record.inventoryUID);

									// If already exists, remove it
									if (exists) {
										return prev.filter((item) => item.inventoryUID !== record.inventoryUID);
									}

									// If it's the first selected, simply add it
									if (prev.length === 0) {
										return [
											{
												inventoryUID: record.inventoryUID,
												deviceID: record.assetID || null,
												orgID: record.orgOrgID || null,
												name: record.inventoryName
											}
										];
									}

									// If there are selected, validate compatibility (all must have or not have orgOrgID)
									const firstIsAssigned = !!prev[0].orgID;
									const currentIsAssigned = !!record.orgOrgID;

									if (firstIsAssigned === currentIsAssigned) {
										// Add only if the state matches
										return [
											...prev,
											{
												inventoryUID: record.inventoryUID,
												deviceID: record.assetID || null,
												orgID: record.orgOrgID || null,
												name: record.inventoryName
											}
										];
									}

									// If it doesn't match, ignore
									return prev;
								});
							}
						})}
					/>
				</Col>
			</Row>

			<AssignInventoryAssets
				workspaces={workspaces}
				devices={selectedInventoryUIDs}
				setDevices={setSelectedInventoryUIDs}
				open={openAssignModal}
				setOpen={setOpenAssignModal}
				refetch={() => dispatch(inventoryActions.loadInventoryLocks())}
				categories={[
					{
						categoryID: constants.SMART_LOCKS,
						name: 'Smart Locks'
					},
					{
						categoryID: constants.SMART_LOCKER,
						name: 'Smart Lockers'
					}
				]}
				platform='TTlock'
				type='Lock'
			>
				<div />
			</AssignInventoryAssets>

			<UnassignInventoryAssets
				devices={selectedInventoryUIDs}
				setDevices={setSelectedInventoryUIDs}
				open={openUnassignModal}
				setOpen={setOpenUnassignModal}
				refetch={() => dispatch(inventoryActions.loadInventoryLocks())}
				platform='TTlock'
				type='Lock'
			>
				<div />
			</UnassignInventoryAssets>

			<EditDeviceLocationModal
				editVisible={visibleEditLocation}
				setEditVisible={setVisibleEditLocation}
				deviceID={selectedDevice.deviceID}
				orgID={selectedDevice.orgID}
				orgName={selectedDevice.orgName}
			>
				<div />
			</EditDeviceLocationModal>
		</div>
	);
};

export default InventoryLocksTable;
